import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["imageInput", "cropModal", "cropImage", "cropBox", "cropArea", "previewCanvas", "saveBtn", "currentImage", "privateAccount", "socialSettings", "profileTab", "settingsTab", "profilePane", "settingsPane"]

  connect() {
    this.originalImage = null
    this.cropData = { x: 0, y: 0, size: 100 }
    this.isDragging = false
    this.isResizing = false
    this.activeHandle = null
    this.startX = 0
    this.startY = 0
    this.startCropX = 0
    this.startCropY = 0
    this.startSize = 0

    this.boundMouseMove = this.handleMouseMove.bind(this)
    this.boundMouseUp = this.handleMouseUp.bind(this)
    this.boundTouchMove = this.handleTouchMove.bind(this)
    this.boundTouchEnd = this.handleTouchEnd.bind(this)

    document.addEventListener("mousemove", this.boundMouseMove)
    document.addEventListener("mouseup", this.boundMouseUp)
    document.addEventListener("touchmove", this.boundTouchMove)
    document.addEventListener("touchend", this.boundTouchEnd)

    if (localStorage.getItem("settingsTab") === "settings") {
      this.activateTab("settings")
    }
  }

  disconnect() {
    document.removeEventListener("mousemove", this.boundMouseMove)
    document.removeEventListener("mouseup", this.boundMouseUp)
    document.removeEventListener("touchmove", this.boundTouchMove)
    document.removeEventListener("touchend", this.boundTouchEnd)
  }

  fileSelected(event) {
    const file = event.target.files[0]
    if (!file) return

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file")
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      this.originalImage = new Image()
      this.originalImage.onload = () => {
        this.cropImageTarget.src = e.target.result
        this.modal = new bootstrap.Modal(this.cropModalTarget)
        this.cropModalTarget.addEventListener("shown.bs.modal", () => {
          setTimeout(() => this.initCropBox(), 50)
        }, { once: true })
        this.cropModalTarget.addEventListener("hidden.bs.modal", () => {
          this.imageInputTarget.value = ""
        }, { once: true })
        this.modal.show()
      }
      this.originalImage.src = e.target.result
    }
    reader.readAsDataURL(file)
  }

  initCropBox() {
    const imgWidth = this.cropImageTarget.offsetWidth
    const imgHeight = this.cropImageTarget.offsetHeight
    const minDim = Math.min(imgWidth, imgHeight)
    this.cropData.size = minDim * 0.8
    this.cropData.x = (imgWidth - this.cropData.size) / 2
    this.cropData.y = (imgHeight - this.cropData.size) / 2
    this.updateCropBox()
    this.updatePreview()
  }

  updateCropBox() {
    this.cropBoxTarget.style.left = this.cropData.x + "px"
    this.cropBoxTarget.style.top = this.cropData.y + "px"
    this.cropBoxTarget.style.width = this.cropData.size + "px"
    this.cropBoxTarget.style.height = this.cropData.size + "px"
  }

  updatePreview() {
    if (!this.originalImage) return

    const ctx = this.previewCanvasTarget.getContext("2d")
    const scaleX = this.originalImage.width / this.cropImageTarget.offsetWidth
    const scaleY = this.originalImage.height / this.cropImageTarget.offsetHeight

    const sourceX = this.cropData.x * scaleX
    const sourceY = this.cropData.y * scaleY
    const sourceSize = this.cropData.size * scaleX

    ctx.clearRect(0, 0, 80, 80)
    ctx.drawImage(this.originalImage, sourceX, sourceY, sourceSize, sourceSize, 0, 0, 80, 80)
  }

  cropBoxTargetConnected(element) {
    element.addEventListener("mousedown", (e) => this.handleCropMouseDown(e))
    element.addEventListener("touchstart", (e) => this.handleCropTouchStart(e))
  }

  handleCropMouseDown(e) {
    if (e.target.classList.contains("crop-handle")) {
      this.isResizing = true
      this.activeHandle = e.target.dataset.handle
    } else {
      this.isDragging = true
    }
    this.startX = e.clientX
    this.startY = e.clientY
    this.startCropX = this.cropData.x
    this.startCropY = this.cropData.y
    this.startSize = this.cropData.size
    e.preventDefault()
  }

  handleCropTouchStart(e) {
    const touch = e.touches[0]
    if (e.target.classList.contains("crop-handle")) {
      this.isResizing = true
      this.activeHandle = e.target.dataset.handle
    } else {
      this.isDragging = true
    }
    this.startX = touch.clientX
    this.startY = touch.clientY
    this.startCropX = this.cropData.x
    this.startCropY = this.cropData.y
    this.startSize = this.cropData.size
    e.preventDefault()
  }

  handleMouseMove(e) {
    if (!this.isDragging && !this.isResizing) return
    this.processDrag(e.clientX, e.clientY)
  }

  handleTouchMove(e) {
    if (!this.isDragging && !this.isResizing) return
    const touch = e.touches[0]
    this.processDrag(touch.clientX, touch.clientY)
  }

  processDrag(clientX, clientY) {
    const dx = clientX - this.startX
    const dy = clientY - this.startY

    if (this.isDragging) {
      this.cropData.x = Math.max(0, Math.min(this.startCropX + dx, this.cropImageTarget.offsetWidth - this.cropData.size))
      this.cropData.y = Math.max(0, Math.min(this.startCropY + dy, this.cropImageTarget.offsetHeight - this.cropData.size))
    } else if (this.isResizing) {
      let newSize = this.startSize
      let newX = this.startCropX
      let newY = this.startCropY
      const delta = Math.max(Math.abs(dx), Math.abs(dy))
      const sign = (dx + dy) > 0 ? 1 : -1

      if (this.activeHandle === "se") {
        newSize = Math.max(50, this.startSize + sign * delta)
      } else if (this.activeHandle === "nw") {
        newSize = Math.max(50, this.startSize - sign * delta)
        newX = this.startCropX + (this.startSize - newSize)
        newY = this.startCropY + (this.startSize - newSize)
      } else if (this.activeHandle === "ne") {
        const d = dx - dy
        newSize = Math.max(50, this.startSize + d / 2)
        newY = this.startCropY + (this.startSize - newSize)
      } else if (this.activeHandle === "sw") {
        const d = dy - dx
        newSize = Math.max(50, this.startSize + d / 2)
        newX = this.startCropX + (this.startSize - newSize)
      }

      if (newX >= 0 && newY >= 0 &&
          newX + newSize <= this.cropImageTarget.offsetWidth &&
          newY + newSize <= this.cropImageTarget.offsetHeight) {
        this.cropData.size = newSize
        this.cropData.x = newX
        this.cropData.y = newY
      }
    }

    this.updateCropBox()
    this.updatePreview()
  }

  handleMouseUp() {
    this.isDragging = false
    this.isResizing = false
    this.activeHandle = null
  }

  handleTouchEnd() {
    this.isDragging = false
    this.isResizing = false
    this.activeHandle = null
  }

  showProfile() {
    this.activateTab("profile")
    localStorage.setItem("settingsTab", "profile")
  }

  showSettings() {
    this.activateTab("settings")
    localStorage.setItem("settingsTab", "settings")
  }

  activateTab(tab) {
    const isSettings = tab === "settings"
    this.profilePaneTarget.classList.toggle("d-none", isSettings)
    this.settingsPaneTarget.classList.toggle("d-none", !isSettings)
    this.profileTabTarget.classList.toggle("active", !isSettings)
    this.settingsTabTarget.classList.toggle("active", isSettings)
  }

  togglePrivateAccount() {
    this.socialSettingsTargets.forEach(el => {
      el.classList.toggle("d-none", this.privateAccountTarget.checked)
    })
  }

  saveCrop() {
    const canvas = document.createElement("canvas")
    canvas.width = 80
    canvas.height = 80
    const ctx = canvas.getContext("2d")

    const scaleX = this.originalImage.width / this.cropImageTarget.offsetWidth
    const scaleY = this.originalImage.height / this.cropImageTarget.offsetHeight

    const sourceX = this.cropData.x * scaleX
    const sourceY = this.cropData.y * scaleY
    const sourceSize = this.cropData.size * scaleX

    ctx.drawImage(this.originalImage, sourceX, sourceY, sourceSize, sourceSize, 0, 0, 80, 80)

    const imageData = canvas.toDataURL("image/jpeg", 0.95)

    this.saveBtnTarget.disabled = true
    this.saveBtnTarget.textContent = "Saving..."

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    fetch("/user/upload_image", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken
      },
      body: "image_data=" + encodeURIComponent(imageData)
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        if (this.hasCurrentImageTarget) {
          this.currentImageTarget.src = data.image_url
        } else {
          const wrapper = document.createElement("div")
          wrapper.className = "current-image mb-3"
          const img = document.createElement("img")
          img.src = data.image_url
          img.alt = "Profile Image"
          img.className = "profile-image-preview"
          img.setAttribute("data-profile-target", "currentImage")
          wrapper.appendChild(img)
          const section = this.element.querySelector(".profile-image-section")
          section.insertBefore(wrapper, section.firstChild)
        }
        this.modal.hide()
        this.imageInputTarget.value = ""
      } else {
        alert("Error saving image: " + data.error)
      }
    })
    .catch(error => {
      alert("Error saving image: " + error)
    })
    .finally(() => {
      this.saveBtnTarget.disabled = false
      this.saveBtnTarget.textContent = "Save Image"
    })
  }
}
